import React, { useCallback, useEffect, useState } from 'react';
import { Apple, Smartphone, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/shared/ConfirmDialog';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  AppConfig,
  AppPlatform,
  LocalizedMessage,
  UpdateAppConfigPayload,
  appConfigApi,
} from '../../api/appConfig';

interface AppConfigPageProps {
  isDark: boolean;
}

type Lang = 'ru' | 'uz' | 'kiril';
const LANG_LABEL: Record<Lang, string> = { ru: 'Русский', uz: 'O\'zbek', kiril: 'Ўзбек (кир.)' };

const EMPTY: LocalizedMessage = { ru: '', uz: '', kiril: '' };

function platformLabel(p: AppPlatform): string {
  return p === 'ios' ? 'iOS' : 'Android';
}

export default function AppConfigPage({ isDark }: AppConfigPageProps) {
  const confirm = useConfirm();
  const [configs, setConfigs] = useState<AppConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingPlatform, setSavingPlatform] = useState<AppPlatform | null>(null);
  const [drafts, setDrafts] = useState<Record<AppPlatform, AppConfig | null>>({
    ios: null,
    android: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await appConfigApi.list();
      setConfigs(list);
      const drafts: Record<AppPlatform, AppConfig | null> = { ios: null, android: null };
      for (const c of list) drafts[c.platform] = c;
      setDrafts(drafts);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось загрузить конфиг');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateDraft = (platform: AppPlatform, patch: Partial<AppConfig>) => {
    setDrafts(prev => ({
      ...prev,
      [platform]: prev[platform] ? { ...prev[platform]!, ...patch } : prev[platform],
    }));
  };

  const updateLocalized = (
    platform: AppPlatform,
    field: 'force_update_message' | 'soft_update_message' | 'maintenance_title' | 'maintenance_message',
    lang: Lang,
    value: string,
  ) => {
    const current = drafts[platform];
    if (!current) return;
    updateDraft(platform, {
      [field]: { ...(current[field] || EMPTY), [lang]: value },
    } as Partial<AppConfig>);
  };

  const save = async (platform: AppPlatform) => {
    const draft = drafts[platform];
    if (!draft) return;

    // Подтверждение при ВКЛЮЧЕНИИ режима обслуживания — он блокирует приложение всем.
    const wasMaintenance = configs.find(c => c.platform === platform)?.maintenance_mode ?? false;
    if (draft.maintenance_mode && !wasMaintenance) {
      const ok = await confirm({
        title: 'Включить режим обслуживания?',
        description: `Приложение (${platformLabel(platform)}) будет заблокировано для всех пользователей экраном обслуживания.`,
        confirmLabel: 'Включить',
        destructive: true,
      });
      if (!ok) return;
    }

    setSavingPlatform(platform);
    try {
      const payload: UpdateAppConfigPayload = {
        latest_version: draft.latest_version,
        min_required_version: draft.min_required_version,
        store_url: draft.store_url,
        force_update_message: draft.force_update_message,
        soft_update_message: draft.soft_update_message,
        maintenance_title: draft.maintenance_title,
        maintenance_message: draft.maintenance_message,
        maintenance_mode: draft.maintenance_mode,
        telegram_login_enabled: !!draft.telegram_login_enabled,
      };
      const updated = await appConfigApi.update(platform, payload);
      updateDraft(platform, updated);
      setConfigs(prev => prev.map(c => (c.platform === platform ? updated : c)));
      toast.success(`${platformLabel(platform)}: сохранено`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setSavingPlatform(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center pt-32">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  return (
    <div className="pt-16 lg:p-8 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className={cn('text-2xl font-bold', isDark ? 'text-white' : 'text-[#111111]')}>
          Конфигурация приложения
        </h1>
        <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
          Управление версиями, режимом обслуживания и сообщениями для мобильных приложений iOS / Android.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {(['ios', 'android'] as AppPlatform[]).map(platform => {
          const draft = drafts[platform];
          if (!draft) return null;
          const Icon = platform === 'ios' ? Apple : Smartphone;
          return (
            <Card
              key={platform}
              className={cn(
                'p-6 rounded-2xl border-0 space-y-6',
                isDark ? 'bg-[#1A1A22]' : 'bg-white',
              )}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#C9A227]/10 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-[#C9A227]" />
                </div>
                <div className="flex-1">
                  <h2 className={cn('text-lg font-bold', isDark ? 'text-white' : 'text-[#111111]')}>
                    {platformLabel(platform)}
                  </h2>
                  <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Обновлено: {new Date(draft.updated_at).toLocaleString('ru-RU')}
                  </p>
                </div>
              </div>

              <div
                className={cn(
                  'flex items-center justify-between p-4 rounded-xl border-2 transition-colors',
                  draft.maintenance_mode
                    ? 'border-amber-500/40 bg-amber-500/5'
                    : isDark
                    ? 'border-slate-700'
                    : 'border-slate-200',
                )}
              >
                <div>
                  <Label
                    className={cn(
                      'font-semibold',
                      isDark ? 'text-white' : 'text-[#111111]',
                    )}
                  >
                    Режим обслуживания
                  </Label>
                  <p className={cn('text-xs mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    При включении приложение полностью блокируется и показывает заголовок с сообщением ниже.
                  </p>
                </div>
                <Switch
                  checked={draft.maintenance_mode}
                  onCheckedChange={v => updateDraft(platform, { maintenance_mode: v })}
                />
              </div>

              <div
                className={cn(
                  'flex items-center justify-between gap-4 p-4 rounded-xl border-2 transition-colors',
                  draft.telegram_login_enabled
                    ? 'border-sky-500/40 bg-sky-500/5'
                    : isDark
                    ? 'border-slate-700'
                    : 'border-slate-200',
                )}
              >
                <div>
                  <Label
                    className={cn(
                      'font-semibold',
                      isDark ? 'text-white' : 'text-[#111111]',
                    )}
                  >
                    Вход через Telegram
                  </Label>
                  <p className={cn('text-xs mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Показывает кнопку Telegram на экране входа. Выключите, если бот или Telegram
                    недоступны — кнопка спрячется без обновления приложения.
                  </p>
                </div>
                <Switch
                  checked={!!draft.telegram_login_enabled}
                  onCheckedChange={v => updateDraft(platform, { telegram_login_enabled: v })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                    Последняя версия (в сторе)
                  </Label>
                  <Input
                    value={draft.latest_version}
                    onChange={e => updateDraft(platform, { latest_version: e.target.value })}
                    placeholder="1.2.0"
                    className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                    Минимально допустимая
                  </Label>
                  <Input
                    value={draft.min_required_version}
                    onChange={e =>
                      updateDraft(platform, { min_required_version: e.target.value })
                    }
                    placeholder="1.0.0"
                    className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}
                  />
                  <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    Ниже этой версии — Force Update (блокирующий экран).
                  </p>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                    Ссылка на стор
                  </Label>
                  <Input
                    value={draft.store_url}
                    onChange={e => updateDraft(platform, { store_url: e.target.value })}
                    placeholder={
                      platform === 'ios'
                        ? 'https://apps.apple.com/app/...'
                        : 'https://play.google.com/store/apps/details?id=...'
                    }
                    className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}
                  />
                </div>
              </div>

              <Tabs defaultValue="force">
                <TabsList className={cn('w-full', isDark ? 'bg-slate-800' : 'bg-slate-100')}>
                  <TabsTrigger value="force" className="flex-1">
                    Force Update
                  </TabsTrigger>
                  <TabsTrigger value="soft" className="flex-1">
                    Soft Update
                  </TabsTrigger>
                  <TabsTrigger value="maintenance" className="flex-1">
                    Maintenance
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="force" className="space-y-3 pt-4">
                  <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Сообщение на блокирующем экране, когда версия &lt; минимально допустимой.
                  </p>
                  {(['ru', 'uz', 'kiril'] as Lang[]).map(lang => (
                    <div key={lang} className="space-y-1">
                      <Label
                        className={cn(
                          'text-xs uppercase tracking-wide',
                          isDark ? 'text-slate-400' : 'text-slate-500',
                        )}
                      >
                        {LANG_LABEL[lang]}
                      </Label>
                      <Textarea
                        value={draft.force_update_message?.[lang] || ''}
                        onChange={e =>
                          updateLocalized(platform, 'force_update_message', lang, e.target.value)
                        }
                        rows={2}
                        className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}
                      />
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="soft" className="space-y-3 pt-4">
                  <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Сообщение во всплывающей модалке (можно закрыть и продолжить пользоваться).
                  </p>
                  {(['ru', 'uz', 'kiril'] as Lang[]).map(lang => (
                    <div key={lang} className="space-y-1">
                      <Label
                        className={cn(
                          'text-xs uppercase tracking-wide',
                          isDark ? 'text-slate-400' : 'text-slate-500',
                        )}
                      >
                        {LANG_LABEL[lang]}
                      </Label>
                      <Textarea
                        value={draft.soft_update_message?.[lang] || ''}
                        onChange={e =>
                          updateLocalized(platform, 'soft_update_message', lang, e.target.value)
                        }
                        rows={2}
                        className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}
                      />
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="maintenance" className="space-y-4 pt-4">
                  <div>
                    <Label
                      className={cn(
                        'text-sm font-semibold mb-2 block',
                        isDark ? 'text-slate-200' : 'text-slate-700',
                      )}
                    >
                      Заголовок
                    </Label>
                    {(['ru', 'uz', 'kiril'] as Lang[]).map(lang => (
                      <div key={lang} className="space-y-1 mb-2">
                        <Label
                          className={cn(
                            'text-xs uppercase tracking-wide',
                            isDark ? 'text-slate-400' : 'text-slate-500',
                          )}
                        >
                          {LANG_LABEL[lang]}
                        </Label>
                        <Input
                          value={draft.maintenance_title?.[lang] || ''}
                          onChange={e =>
                            updateLocalized(
                              platform,
                              'maintenance_title',
                              lang,
                              e.target.value,
                            )
                          }
                          className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label
                      className={cn(
                        'text-sm font-semibold mb-2 block',
                        isDark ? 'text-slate-200' : 'text-slate-700',
                      )}
                    >
                      Сообщение
                    </Label>
                    {(['ru', 'uz', 'kiril'] as Lang[]).map(lang => (
                      <div key={lang} className="space-y-1 mb-2">
                        <Label
                          className={cn(
                            'text-xs uppercase tracking-wide',
                            isDark ? 'text-slate-400' : 'text-slate-500',
                          )}
                        >
                          {LANG_LABEL[lang]}
                        </Label>
                        <Textarea
                          value={draft.maintenance_message?.[lang] || ''}
                          onChange={e =>
                            updateLocalized(
                              platform,
                              'maintenance_message',
                              lang,
                              e.target.value,
                            )
                          }
                          rows={3}
                          className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : '')}
                        />
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => save(platform)}
                  disabled={savingPlatform === platform}
                  className="bg-[#C9A227] hover:bg-[#9C7E1B] text-white"
                >
                  {savingPlatform === platform ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Сохранить
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
